<template>
  <div class="modal fade" id="sectionFormModal" tabindex="-1" aria-labelledby="blockFormModalLabel"
    :aria-hidden="!isModalVisible">
    <div :class="dialogClass">
      <div class="modal-content border-0 shadow-lg">
        <div class="modal-header bg-white border-bottom px-4 py-3">
          <div class="w-100">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h5 class="modal-title fw-bold text-primary mb-0" id="blockFormModalLabel">
                <i :class="isEditing ? 'bi bi-pencil-square' : 'bi bi-plus-circle'" class="me-2"></i>
                {{ isEditing ? "Editar Bloque" : "Nuevo Bloque" }}
              </h5>
              <button type="button" class="btn-close" @click="closeForm" :disabled="isLoading" aria-label="Close"></button>
            </div>
            <div class="d-flex align-items-center gap-3">
              <small class="text-muted text-nowrap">
                Paso {{ currentStep }} de {{ totalSteps }}:
                <span v-if="currentStep === 1">Configuración y Diseño</span>
                <span v-else>Contenido del Bloque</span>
              </small>
              <div class="progress flex-grow-1" style="height: 5px;">
                <div class="progress-bar" role="progressbar" :style="{ width: progressPercent + '%' }" :aria-valuenow="progressPercent" aria-valuemin="0" aria-valuemax="100"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-body" :class="currentStep === 1 ? 'body-step-config' : 'body-step-content'">

          <div v-if="currentStep === 1" class="fade-in">
            <form @submit.prevent="nextStep">
              <div class="row g-4 align-items-stretch">
                <div class="col-md-5 pe-md-4 border-end-md">
                  <h6 class="text-muted border-bottom pb-2 mb-4 small fw-bold text-uppercase">
                    Datos de la Sección
                  </h6>
                  <div class="row g-3">
                    <div class="col-12">
                      <ToggleField label="Nombre Interno" id="block_name" v-model="blockName" type="input" />
                    </div>
                    <div class="col-12">
                      <ToggleField label="Título Visible" id="block_title" v-model="blockTitle" type="input" />
                    </div>
                    <div class="col-md-8" v-if="SHOW_BUTTON_TEXT_FIELD">
                      <div class="form-floating">
                        <input type="text" id="block_button_text" class="form-control" v-model="blockButtonText"
                          placeholder="Ej: Ver más" />
                        <label for="block_button_text">Texto del Botón (Opcional)</label>
                      </div>
                    </div>
                    <div :class="SHOW_BUTTON_TEXT_FIELD ? 'col-md-4' : 'col-12'">
                      <div class="form-floating">
                        <input type="number" id="block_order" class="form-control" v-model="blockOrder" min="1"
                          placeholder="1" required />
                        <label for="block_order">Orden</label>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-7 d-flex flex-column" style="min-height: 0;">
                  <h6 class="text-muted border-bottom pb-2 mb-3 small fw-bold text-uppercase flex-shrink-0">
                    Selecciona una Estructura
                  </h6>
                  <div class="templates-grid" style="flex: 1 1 0; min-height: 0;">
                    <div v-for="(template, index) in templates" :key="index"
                      @click="selectTemplate(template)"
                      class="template-card p-2 border rounded cursor-pointer position-relative"
                      :class="{ 'selected-template': selectedTemplate && selectedTemplate.image === template.image }">
                      <img :src="getTemplateImagePath(template)" :alt="'Plantilla ' + (index + 1)"
                        class="rounded" />
                      <div v-if="selectedTemplate && selectedTemplate.image === template.image"
                        class="selected-badge fade-in">
                        <i class="bi bi-check-circle-fill text-primary fs-5"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div class="fade-in" v-if="currentStep > 1">
            <StepForm v-for="step in steps" :key="step" :step="step" v-show="currentStep === step"
              :current-step="currentStep" :form="getForm(step)" :selected-option="getSelectedOption(step)"
              :get-template-path="getTemplatePath" :selectedTemplate="selectedTemplate" :is-editing="isEditing"
              :steps="steps" :section-type="getSectionType(step)"
              @prev-step="prevStep" @next-step="nextStep" @update="handleUpdate"
              @validateStep="handleValidation" :disabled="isLoading" />
          </div>

        </div>

        <div class="modal-footer bg-white border-top px-4 py-3 d-flex">
          <button
            v-if="currentStep > 1"
            type="button"
            class="btn btn-outline-secondary px-4 rounded-pill"
            @click="prevStep"
            :disabled="isLoading"
          >
            <i class="bi bi-chevron-left me-1"></i> Anterior
          </button>

          <button
            v-if="!isLastStep"
            type="button"
            class="btn btn-primary px-5 rounded-pill shadow-sm ms-auto"
            @click="nextStep"
            :disabled="isNextDisabled"
          >
            Siguiente <i class="bi bi-chevron-right ms-1"></i>
          </button>

          <button
            v-else
            type="button"
            class="btn btn-success px-5 rounded-pill shadow-sm d-flex align-items-center ms-auto"
            @click="submitForm"
            :disabled="isSaveDisabled"
          >
            <div v-if="isLoading" class="spinner-border text-light me-2" role="status"
              style="width: 1rem; height: 1rem; border-width: 0.15em;"></div>
            <span v-if="!isLoading" class="d-flex align-items-center">
              <i class="bi bi-check-lg me-2"></i>
              {{ isEditing ? "Actualizar" : "Guardar Todo" }}
            </span>
            <span v-else>Guardando...</span>
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, computed } from "vue";
import { Modal } from "bootstrap";
import { createBlock, updateBlock } from "@/services/api/blockService.js";
import StepForm from "@/components/block/StepForm.vue";
import Quill from "quill";
import userConfirmation from "@/utils/userConfirmation.js";
import ToggleField from "@/components/shared/ToggleField.vue";
import { showLoadingAlert, closeLoadingAlert } from "@/utils/loadingAlert.js";

const FORM_MODAL_ID = "sectionFormModal";

export default {
  components: {
    StepForm,
    ToggleField,
  },
  props: {
    view: { type: Object, required: true },
    header: { type: Object, default: null },
  },
  setup(props, { emit }) {
    const SHOW_BUTTON_TEXT_FIELD = false;

    const isModalVisible = ref(false);
    const isEditing = ref(false);
    const currentStep = ref(1);
    const selectedTemplate = ref(null);
    const blockName = ref("");
    const blockTitle = ref("");
    const blockOrder = ref(1);
    const blockId = ref(null);

    const blockButtonText = ref("");

    const isLoading = ref(false);
    const stepValidities = ref({ 2: false, 3: false, 4: false });

    const selectedOptionStep3 = ref("image");
    const selectedOptionStep4 = ref("image");
    const selectedOptionStep5 = ref("image");

    const createFormStep = () => ({
      content: "",
      image: null,
      imagePreview: "",
      view_id: props.view?.id || "",
      alignment: "",
      order: "",
      type: "",
      background_color: "",
    });

    const formStep3 = ref(createFormStep());
    const formStep4 = ref(createFormStep());
    const formStep5 = ref(createFormStep());

    const templates = ref([
      { image: "plantilla_1.png", number: 3, id: 1, type_template_1: 1, type_template_2: 2, type_template_3: 2 },
      { image: "plantilla_2.png", number: 3, id: 2, type_template_1: 2, type_template_2: 2, type_template_3: 1 },
      { image: "plantilla_3.png", number: 2, id: 3, type_template_1: 2, type_template_2: 2 },
      { image: "plantilla_4.png", number: 1, id: 4, type_template_1: 1 },
    ]);

    const populateFormFields = (header) => {
      if (!header) return;
      blockName.value = header.name || "";
      blockTitle.value = header.title || "";
      blockOrder.value = header.order || 1;

      blockButtonText.value = header.button_text || "";

      blockId.value = header.id || null;

      const sections = (header.sections || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const forms = [formStep3, formStep4, formStep5];

      sections.forEach((section, index) => {
        if (forms[index]) {
          forms[index].value = { ...createFormStep(), ...section };
        }
      });

      const template = templates.value.find(
        (t) => t.id === header.block_template?.id
      );
      selectedTemplate.value = template;

      // Garantiza type/order/alignment aunque no vengan en la sección
      if (template) {
        const templateSections = [
          { form: formStep3, type: template.type_template_1, order: 1 },
          { form: formStep4, type: template.type_template_2, order: 2 },
          { form: formStep5, type: template.type_template_3, order: 3 },
        ];
        templateSections.forEach((ts) => {
          if (ts.type != null) {
            if (!ts.form.value.type)      ts.form.value.type      = ts.type;
            if (!ts.form.value.order)     ts.form.value.order     = ts.order;
            if (!ts.form.value.alignment) ts.form.value.alignment = 2;
          }
        });
      }
    };

    const selectTemplate = (template) => {
      selectedTemplate.value = template;
      const stepsConfig = [
        { form: formStep3, type: template.type_template_1, order: 1 },
        { form: formStep4, type: template.type_template_2, order: 2 },
        { form: formStep5, type: template.type_template_3, order: 3 },
      ];
      stepsConfig.forEach((step, index) => {
        if (template.number > index) {
          step.form.value.order = step.order;
          step.form.value.type = step.type;
          step.form.value.alignment = 2;
        }
      });
    };

    const isFirstStepValid = computed(() => {
      return Number.isInteger(Number(blockOrder.value)) && blockOrder.value > 0;
    });

    const steps = computed(() => {
      return selectedTemplate.value
        ? Array.from({ length: selectedTemplate.value.number }, (_, i) => i + 2)
        : [];
    });

    const totalSteps = computed(() => Math.max(2, steps.value.length + 1));

    const forms = computed(() => {
      const activeForms = [formStep3];
      const templateNumber = selectedTemplate.value?.number || 0;
      const sectionsLength = props.header?.sections.length || 0;
      if (templateNumber > 1 || sectionsLength > 1) activeForms.push(formStep4);
      if (templateNumber > 2 || sectionsLength > 2) activeForms.push(formStep5);
      return activeForms;
    });

    const isCurrentStepValid = computed(() => {
      if (currentStep.value === 1) return isFirstStepValid.value && !!selectedTemplate.value;
      return stepValidities.value[currentStep.value] ?? false;
    });

    const isSaveDisabled = computed(() => !isCurrentStepValid.value || isLoading.value);

    const dialogClass = computed(() => [
      'modal-dialog',
      'modal-xl',
      'modal-dialog-centered',
      'modal-dialog-scrollable',
    ]);

    const progressPercent = computed(() =>
      Math.round((currentStep.value / totalSteps.value) * 100)
    );

    const isLastStep = computed(() => currentStep.value === totalSteps.value);

    const isNextDisabled = computed(() => {
      if (isLoading.value) return true;
      return !isCurrentStepValid.value;
    });

    watch(
      () => props.header,
      (newHeader) => {
        if (newHeader) {
          isEditing.value = true;
          populateFormFields(newHeader);
        } else {
          isEditing.value = false;
          blockName.value = "";
          blockTitle.value = "";
          blockOrder.value = 1;
          blockButtonText.value = "";
          selectedTemplate.value = null;
          currentStep.value = 1;
        }
      },
      { immediate: true }
    );

    const getTemplateImagePath = (template) => require(`@/assets/images/${template.image}`);
    const getForm = (step) => (step === 2 ? formStep3.value : step === 3 ? formStep4.value : formStep5.value);
    const getTemplatePath = (number) => {
      if (!selectedTemplate.value) return "";
      const baseName = selectedTemplate.value.image.split(".png")[0];
      return require(`@/assets/images/${baseName}_${number}.png`);
    };
    const getSelectedOption = (step) => (step === 2 ? selectedOptionStep3.value : step === 3 ? selectedOptionStep4.value : selectedOptionStep5.value);

    const getSectionType = (step) => {
      if (!selectedTemplate.value) return null;
      const typeMap = {
        2: selectedTemplate.value.type_template_1,
        3: selectedTemplate.value.type_template_2,
        4: selectedTemplate.value.type_template_3,
      };
      return typeMap[step] ?? null;
    };

    const handleUpdate = ({ key, value, background_color, step }) => {
      const stepsMap = { 2: formStep3, 3: formStep4, 4: formStep5 };
      const currentForm = stepsMap[step]?.value;
      if (currentForm) {
        if (key === "content" && value) {
          const quill = new Quill(document.createElement("div"));
          quill.setContents(value);
          currentForm.content = quill.root.innerHTML;
          currentForm.background_color = background_color;
          currentForm.image = null;
        } else if (key === "image" && value) {
          currentForm.image = value;
          currentForm.content = "";
        }
      }
    };

    const handleValidation = ({ step, isValid }) => {
      if ([2, 3, 4].includes(step)) stepValidities.value[step] = isValid;
    };

    const appendFormData = (formData, form, index) => {
      formData.append(`sections[${index}][id]`, form.id ?? "");
      formData.append(`sections[${index}][content]`, form.content ?? "");
      if (form.image) formData.append(`sections[${index}][image]`, form.image);
      formData.append(`sections[${index}][order]`, form.order != null && form.order !== "" ? form.order : "");
      formData.append(`sections[${index}][type]`, form.type != null && form.type !== "" ? form.type : "");
      formData.append(`sections[${index}][alignment]`, form.alignment != null && form.alignment !== "" ? form.alignment : "");
      formData.append(`sections[${index}][background_color]`, form.background_color ?? "");
    };

    const submitForm = async () => {
      isLoading.value = true;
      showLoadingAlert();

      try {
        const formData = new FormData();
        formData.append("block_name", blockName.value);
        formData.append("block_title", blockTitle.value);

        formData.append("block_button_text", blockButtonText.value);

        formData.append("block_order", blockOrder.value);
        if (blockId.value) formData.append("block_id", blockId.value);
        formData.append("block_status", 1);
        formData.append("block_template", parseInt(selectedTemplate.value.id));
        formData.append("view_id", props.view?.id || "");

        const limitedForms = forms.value.slice(0, selectedTemplate.value.number);
        limitedForms.forEach((form, index) => appendFormData(formData, form.value, index));

        const editOrCreate = isEditing.value ? "editar" : "crear";
        const confirmed = await userConfirmation(`¿Está seguro de ${editOrCreate} el bloque?`);

        if (!confirmed) {
          closeLoadingAlert();
          isLoading.value = false;
          return;
        }

        if (isEditing.value) {
          await updateBlock(props.header.id, formData);
          emit("success", "Bloque actualizado exitosamente.");
        } else {
          await createBlock(formData);
          emit("success", "Bloque creado exitosamente.");
        }

        emit("block-created");
        closeForm();
      } catch (error) {
        emit("error", error.response?.data?.message || "Error al procesar la solicitud.");
      } finally {
        closeLoadingAlert();
        isLoading.value = false;
      }
    };

    const nextStep = () => { if (currentStep.value < totalSteps.value) currentStep.value += 1; };
    const prevStep = () => { if (currentStep.value > 1) currentStep.value -= 1; };

    const closeForm = () => {
      emit("close");
      const modalElement = document.getElementById(FORM_MODAL_ID);
      const modalInstance = Modal.getInstance(modalElement);
      if (modalInstance) modalInstance.hide();
    };

    onMounted(() => {
      const modalElement = document.getElementById(FORM_MODAL_ID);
      const modalInstance = new Modal(modalElement, {
        backdrop: 'static',
        keyboard: false
      });
      modalInstance.show();
      isModalVisible.value = true;
    });

    return {
      isEditing,
      currentStep,
      totalSteps,
      selectedTemplate,
      templates,
      steps,
      blockName,
      blockTitle,
      blockOrder,
      blockButtonText,
      isFirstStepValid,
      formStep3, formStep4, formStep5,
      selectedOptionStep3, selectedOptionStep4, selectedOptionStep5,
      handleUpdate,
      handleValidation,
      submitForm,
      nextStep,
      prevStep,
      closeForm,
      isSaveDisabled,
      isLoading,
      getTemplateImagePath,
      getForm,
      getTemplatePath,
      getSelectedOption,
      getSectionType,
      selectTemplate,
      isModalVisible,
      SHOW_BUTTON_TEXT_FIELD,
      dialogClass,
      progressPercent,
      isLastStep,
      isNextDisabled,
      isCurrentStepValid,
    };
  },
};
</script>

<style scoped>

.fade-in {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.template-card {
  transition: all 0.2s ease;
  border-color: #dee2e6 !important;
  background-color: #fff;
}

.template-card:hover {
  border-color: #0d6efd !important;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.template-card.selected-template {
  border-color: #0d6efd !important;
  background-color: #f0f8ff;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
}

.selected-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  background: white;
  border-radius: 50%;
  line-height: 1;
}

.cursor-pointer {
  cursor: pointer;
}

.body-step-config {
  padding: 2rem 3rem;
}

.body-step-content {
  padding: 0;
  overflow-y: auto;
}

@media (min-width: 768px) {
  .border-end-md {
    border-right: 1px solid #dee2e6;
  }
}

.templates-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 8px;
  min-height: 0;
}

.templates-grid .template-card {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  overflow: hidden;
}

.templates-grid .template-card img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>